import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { isDesktopBrowser } from './constants/deviceDetect';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import SmartProductImage from './SmartProductImage';
import CameraScanner from './CameraScanner';
import { authenticatedFetch, pressingFetch, getPresserSession, clearPressingSession, useAuth } from './AuthComponent';
import { useStore } from './StoreContext';
import { useBarcodeScanner, useIdleLock } from './hooks';
import { synthesizeJumbledBundleNotifications } from './jumbledBundleUtils';
import { playSuccess, playError, preloadSounds } from './sounds';
import { buildBundles, augmentBundleSiblings } from './pressingHelpers';
import { getPreviewImageUrls } from './previewImage';
import QCBoard from './QCBoard';
import PortalParticipantsFooter from './PortalParticipantsFooter';
import { useT, tImperative } from './i18n/I18nContext';
import { LocationPickerModal } from './PickingPortal';
import NotificationBroadcastModal from './NotificationBroadcastModal';
import PortalDailySummary from './PortalDailySummary';

const IDLE_LOCK_MS = 15 * 60 * 1000;

const VIRTUAL_TITLES = new Set(['free returns + package protection', 'processing fee']);
const isVirtual = (title) => VIRTUAL_TITLES.has((title || '').toLowerCase());
// Shared kiosk PIN login writes sessionStorage.presser_session for ALL portal
// types — there is no separate packer_session. See memory
// reference_portal_session_storage_keys (the earlier bug that broke QCBoard
// scan-only gating).
const SESSION_KEY = 'presser_session';
const TOKEN_KEY   = 'pressing_admin_token';

function getSession() {
    try { return JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null'); } catch { return null; }
}

// ── Packing units builder ──────────────────────────────────────────────────────
const EMBELLISHMENT_TYPES = new Set(['Logo', 'LOGO', 'Name', 'Number', 'Embroidered Logo', 'Embroidered Number', 'Crest', 'Decals']);

function getAttr(attrs, ...keys) {
    for (const k of keys) {
        const kLower = k.toLowerCase();
        const found = attrs.find(a => a.key === k || a.key.toLowerCase() === kLower);
        if (found?.value) return found.value;
    }
    return null;
}

/**
 * Build the list of packing units from a Shopify order object.
 * Returns one entry per physical unit to QC:
 *  - Bundles (lineItemGroup items with embellishments) → one per quantity instance
 *  - Individual items (no embellishment) → one per quantity instance
 */
function getPackingUnits(order, shopifyLocationId) {
    if (!order) return [];

    // Find open FO line items for this location. Strict-match by location for
    // packer sessions (which always carry a shopifyLocationId). When empty —
    // admin viewing directly without emulating — fall back to "all
    // OPEN/IN_PROGRESS FOs" so the bundles surface regardless of location.
    const fos = (order.fulfillmentOrders?.edges || []).map(e => e.node);
    const locGid = shopifyLocationId ? `gid://shopify/Location/${shopifyLocationId}` : null;
    const openFoLineItems = {};
    const remainingByLiId = {};
    for (const fo of fos) {
        const assignedId = fo.assignedLocation?.location?.id || '';
        if (locGid && assignedId !== locGid) continue;
        if (!['OPEN', 'IN_PROGRESS'].includes(fo.status)) continue;
        for (const e of (fo.lineItems?.edges || [])) {
            const id = e.node.lineItem?.id;
            const rem = e.node.remainingQuantity || 0;
            if (id && rem > 0) {
                openFoLineItems[id] = e.node;
                remainingByLiId[id] = (remainingByLiId[id] || 0) + rem;
            }
        }
    }

    // Business rule: legacy Shopify bundles (lineItemGroup with no variantSku)
    // collapse all components into a single QC card. "Juventus Camps" orders
    // ship as a 3-item bundle (jersey + shorts + socks) that needs per-item
    // QC — strip lineItemGroup so each item becomes its own standalone card.
    const LEGACY_BUNDLE_TAGS = new Set(['Juventus Camps']);
    const splitLegacyBundle = (order?.tags || []).some(t => LEGACY_BUNDLE_TAGS.has(t));

    const allLineItems = (order.lineItems?.edges || []).map(e => e.node)
        .filter(li => !isVirtual(li.title))
        .filter(li => (li.currentQuantity ?? li.quantity ?? 0) > 0)
        .map(li => splitLegacyBundle ? { ...li, lineItemGroup: null } : li);
    // Strict match + bundle-sibling augmentation: see augmentBundleSiblings
    // docstring. Pulls orphan-bundle children back so QC sees the full kit.
    const relevant = augmentBundleSiblings(
        allLineItems.filter(li => openFoLineItems[li.id]),
        allLineItems,
        remainingByLiId
    );

    // Core grouping — shared with OrderDetail / Pressing / OrderPrintCard
    const groups = buildBundles(relevant, { tags: order?.tags }).map(b => ({
        gid: b.gid,
        items: [b.parent, ...b.children],
        groupData: b.groupData,
    }));

    // Order-level player→number fallback. Some carts only put "Player Name" on
    // the line item — the number lives on _soccer_order_info. Map name→number
    // so the QC card shows "#50" next to "Kai Slocum" even when no Player Number
    // attr was attached to the line item.
    const playerNumberByName = {};
    try {
        const raw = (order?.customAttributes || []).find(a => a.key === '_soccer_order_info')?.value;
        if (raw) {
            for (const p of (JSON.parse(raw) || [])) {
                const nm = p?.name;
                // Broken-order team-bulk: assigned_number array trumps singular config.number.
                const assigned = p?.config?.assigned_number;
                if (Array.isArray(assigned) && assigned.length > 0) {
                    const nums = assigned.map(n => String(n).trim()).filter(Boolean);
                    if (nm && nums.length) {
                        try { nums.sort((a, b) => parseInt(a, 10) - parseInt(b, 10)); } catch (_) {}
                        playerNumberByName[nm] = nums.join(', ');
                        continue;
                    }
                }
                const num = p?.config?.number ?? p?.number;
                if (nm && num != null && String(num).trim() !== '') {
                    playerNumberByName[nm] = String(num).trim();
                }
            }
        }
    } catch (_) { /* malformed JSON → no fallback */ }

    // Build packing units — expand by quantity
    const units = [];
    for (const g of groups) {
        // isBundle = true only when group has BOTH an embellishment AND a garment.
        // Orphaned embellishments (no garment) appear as isBundle=false solo items — intentional.
        const isBundle = g.items.some(li => EMBELLISHMENT_TYPES.has(li.productType)) &&
                         g.items.some(li => !EMBELLISHMENT_TYPES.has(li.productType));
        const parent = g.items.find(li => li.sku === g.groupData?.variantSku)
            || g.items.find(li => !EMBELLISHMENT_TYPES.has(li.productType))
            || g.items[0];
        const qty = parent?.quantity || 1;

        const groupAttrs   = g.groupData?.customAttributes || [];
        const allItemAttrs = g.items.flatMap(li => li.customAttributes || []);
        const playerName   = getAttr(groupAttrs, 'Player Name', 'player_name')
            || getAttr(allItemAttrs, 'Player Name', 'player_name');
        const playerNumber = getAttr(groupAttrs, 'Player Number', 'player_number', 'Number', '#')
            || getAttr(allItemAttrs, 'Player Number', 'player_number', 'Number', '#')
            || (playerName ? playerNumberByName[playerName] : '')
            || '';

        const barcodes = g.items
            .map(li => li.variant?.barcode)
            .filter(Boolean);

        const foLineItemIds = g.items
            .map(li => openFoLineItems[li.id]?.id)
            .filter(Boolean);

        const group_key = parent?.sku || g.gid;

        const children = g.items.filter(li => li !== parent);
        // Map each SKU → line item quantity, for per-instance pick checking
        const skuQtyMap = {};
        for (const li of g.items) {
            if (li.sku) skuQtyMap[li.sku] = li.quantity || 1;
        }
        const childImages = children
            .map(li => ({ url: li.image?.url || null, title: li.title || '' }))
            .filter(c => c.url);

        const allAttrs = [...groupAttrs, ...allItemAttrs];
        const { front: previewFront, back: previewBack } = getPreviewImageUrls(allAttrs);

        for (let i = 0; i < qty; i++) {
            units.push({
                unitKey:        `${g.gid}__${i}`,
                group_key,
                gid:            g.gid,
                title:          parent?.title || tImperative('Item'),
                productType:    parent?.productType || '',
                variantTitle:   parent?.variant?.title && parent.variant.title !== 'Default Title' ? parent.variant.title : null,
                sku:            parent?.sku || null,
                imageUrl:       parent?.image?.url || null,
                previewFront,
                previewBack,
                childImages,
                playerName,
                playerNumber,
                isBundle,
                barcodes,
                skuQtyMap,
                instanceIndex:  i + 1,
                totalInstances: qty,
                foLineItemIds,
            });
        }
    }
    return units;
}

// 2026-07-29 SP-297179: Under-scan guard. Sum remainingQuantity across ALL
// OPEN/IN_PROGRESS FOs at this location (bundles + base items + embellishments),
// excluding virtual-only FOs (TPHF/Processing Fee/Free Returns) and pickup FOs
// already parked at their destination. Mirrors the server's
// `_total_open_units_at_location` so the FE's belief and the server's
// cross-check compare apples to apples. Used by FulfillmentSummary to decide
// whether to surface the "ship as partial" intercept modal before calling
// /complete.
export function computeTotalOpenUnitsAtLocation(order, shopifyLocationId) {
    if (!shopifyLocationId || !order) return 0;
    // 2026-07-30: exclude individual virtual line items (Free Returns +
    // Package Protection / Processing Fee) that piggy-back onto real FOs —
    // the packing portal's scan list already excludes them
    // (getPackingUnits uses `isVirtual`), so counting them here would trip
    // the under-scan modal on orders whose only "missing" units are virtual
    // fees. Reported as "orders with Redo can't be packed — showing as
    // partial". Mirrors backend `_total_open_units_at_location`.
    const titleByOli = {};
    for (const e of (order.lineItems?.edges || [])) {
        const n = e.node || {};
        if (n.id) titleByOli[n.id] = (n.title || '');
    }
    const locGid = `gid://shopify/Location/${shopifyLocationId}`;
    const fos = (order.fulfillmentOrders?.edges || []).map(e => e.node);
    let total = 0;
    for (const fo of fos) {
        if (!['OPEN', 'IN_PROGRESS'].includes(fo.status)) continue;
        const assignedId = fo.assignedLocation?.location?.id || '';
        if (assignedId !== locGid) continue;
        if (fo.isVirtualOnly) continue;
        if (fo._packedHere) continue;
        const dm = fo.deliveryMethod || {};
        // Franchise stores (backend flag false) fulfill their own pickups —
        // the FO is work there, not parked (SP-341768, 2026-09-05).
        if (dm.methodType === 'PICK_UP' && fo._assignedIsRetailDest !== false) {
            const assigned = (fo.assignedLocation?.name || '').trim();
            const presented = (dm.presentedName || '').trim();
            if (assigned && presented && assigned === presented) continue;
        }
        for (const lie of (fo.lineItems?.edges || [])) {
            const n = lie.node || {};
            const oli = n.lineItem?.id;
            if (isVirtual(titleByOli[oli])) continue;
            total += (n.remainingQuantity || 0);
        }
    }
    return total;
}

// ── PIN Login ──────────────────────────────────────────────────────────────────
function PinLogin({ onSuccess }) {
    const [locations, setLocations] = useState([]);
    const [locationId, setLocationId] = useState('');
    const [pin, setPin] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        authenticatedFetch('/api/locations')
            .then(r => r.json())
            .then(setLocations)
            .catch(() => {});
    }, []);

    const submit = async () => {
        if (!locationId || pin.length < 4) return;
        setLoading(true); setError('');
        try {
            const r = await authenticatedFetch('/api/pressing/verify-pin', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ shopify_location_id: locationId, pin, portal_type: 'packing' }),
            });
            const d = await r.json();
            if (!r.ok) { setError(d.detail || 'Invalid PIN'); return; }
            sessionStorage.setItem(SESSION_KEY, JSON.stringify(d));
            onSuccess(d);
        } catch { setError('Network error'); }
        finally { setLoading(false); }
    };

    // PIN login screen stays English by design (per user spec).
    return (
        <div className="qcp-pin-screen">
            <div className="qcp-pin-card">
                <div className="qcp-pin-title">📦 Packing Portal</div>
                <select className="qcp-pin-select" value={locationId} onChange={e => setLocationId(e.target.value)}>
                    <option value="">Select location…</option>
                    {locations.map(l => <option key={l.shopify_location_id} value={l.shopify_location_id}>{l.name}</option>)}
                </select>
                <input
                    className="qcp-pin-input"
                    type="password"
                    inputMode="numeric"
                    placeholder="Enter PIN"
                    maxLength={6}
                    value={pin}
                    onChange={e => setPin(e.target.value.replace(/\D/g, ''))}
                    onKeyDown={e => e.key === 'Enter' && submit()}
                />
                {error && <div className="qcp-pin-error">{error}</div>}
                <button className="qcp-pin-btn" onClick={submit} disabled={loading || !locationId || pin.length < 4}>
                    {loading ? 'Checking…' : 'Enter'}
                </button>
            </div>
        </div>
    );
}

// ── Order Search ───────────────────────────────────────────────────────────────
function OrderSearch({ session, presserSession, assignments, locationMap, onSelectOrder, sidebarMode = false, fetchFn = authenticatedFetch, belowInput = null }) {
    const t = useT();
    const [query, setQuery]       = useState('');
    const [results, setResults]   = useState([]);
    const [loading, setLoading]   = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        // Belt-and-suspenders focus: fire immediately on mount, then again
        // after a beat to win any focus race with sibling elements (camera
        // button, scroll restore, etc) that mount in the same frame.
        inputRef.current?.focus();
        const t1 = setTimeout(() => inputRef.current?.focus(), 50);
        const t2 = setTimeout(() => inputRef.current?.focus(), 250);
        preloadSounds();
        return () => { clearTimeout(t1); clearTimeout(t2); };
    }, []);

    // Keep search input focused for Bluetooth scanner support.
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        const handleBlur = () => { setTimeout(() => el.focus(), 50); };
        el.addEventListener('blur', handleBlur);
        return () => el.removeEventListener('blur', handleBlur);
    }, []);

    const showBadge = (presserSession && assignments && assignments.length > 1);

    const search = useCallback((q) => {
        if (!q || q.trim().length < 2) { setResults([]); return; }
        setLoading(true);

        // Admin sidebar / legacy single-location path
        if (!presserSession || !assignments || assignments.length === 0) {
            fetchFn(`/api/packing/orders?shopify_location_id=${encodeURIComponent(session.shopify_location_id)}&q=${encodeURIComponent(q.trim())}`)
                .then(r => r.json())
                .then(data => {
                    if (data.length === 1) { onSelectOrder(data[0]); } else { setResults(data); }
                })
                .catch(() => setResults([]))
                .finally(() => setLoading(false));
            return;
        }

        // Multi-location presser: fan out per distinct store_key
        const byStore = {};
        for (const a of assignments) {
            if (!byStore[a.store_key]) byStore[a.store_key] = [];
            byStore[a.store_key].push(a.shopify_location_id);
        }
        const storeKeys = Object.keys(byStore);
        const requests = storeKeys.map(sk => {
            const csv = byStore[sk].join(',');
            return fetchFn(
                `/api/packing/orders?shopify_location_ids=${encodeURIComponent(csv)}&q=${encodeURIComponent(q.trim())}`,
                undefined,
                sk,
            )
                .then(r => r.ok ? r.json() : [])
                .then(list => (Array.isArray(list) ? list : []).map(o => {
                    const locInfo = locationMap.get(String(o.shopify_location_id)) || {};
                    return {
                        ...o,
                        __store_key:     sk,
                        __location_name: locInfo.location_name || '',
                    };
                }))
                .catch(() => []);
        });
        Promise.all(requests)
            .then(lists => {
                const merged = lists.flat();
                if (merged.length === 1) { onSelectOrder(merged[0]); } else { setResults(merged); }
            })
            .catch(() => setResults([]))
            .finally(() => setLoading(false));
    }, [session.shopify_location_id, fetchFn, onSelectOrder, presserSession, assignments, locationMap]);

    const handleScan = useCallback((code) => {
        setQuery(code);
        search(code);
    }, [search]);

    useBarcodeScanner(handleScan);

    return (
        <div className={sidebarMode ? 'os-wrapper' : 'qcp-search-screen'}>
            {cameraOpen && (
                <CameraScanner
                    onDetect={(barcode) => { setCameraOpen(false); setQuery(barcode); search(barcode); }}
                    onClose={() => setCameraOpen(false)}
                />
            )}
            {!sidebarMode && (
                <div className="qcp-header qcp-header--search">
                    <div className="qcp-header-title">📦 {t('Packing Portal')}</div>
                    <div className="qcp-header-sub">
                        {assignments && assignments.length > 1
                            ? t('Multiple locations ({n})', { n: assignments.length })
                            : (assignments && assignments.length === 1 ? assignments[0].location_name : session.location_name)}
                    </div>
                    <div className="qcp-header-sub">
                        {session.first_name} {session.last_name}
                    </div>
                    <button className="qcp-sign-out" onClick={() => { sessionStorage.removeItem('presser_session'); window.location.href = '/pressing-portal/login'; }}>{t('Sign out')}</button>
                </div>
            )}
            <div className={sidebarMode ? null : 'qcp-search-body'}>
                {sidebarMode ? (
                    <div className="os-input-wrap">
                        <input
                            ref={inputRef}
                            autoFocus
                            className="os-input"
                            placeholder={t('Search by customer name or order number…')}
                            value={query}
                            onChange={e => { setQuery(e.target.value); search(e.target.value); }}
                        />
                        <button className="pkp-camera-btn" onClick={() => setCameraOpen(true)} title={t('Scan order barcode')}>📷</button>
                        <span className="os-search-icon">🔍</span>
                    </div>
                ) : (
                    <div className="os-input-wrap" style={{ marginBottom: 12 }}>
                        <input
                            ref={inputRef}
                            autoFocus
                            className="os-input"
                            placeholder={t('Search order or scan barcode…')}
                            value={query}
                            onChange={e => { setQuery(e.target.value); search(e.target.value); }}
                        />
                        <button className="pkp-camera-btn" onClick={() => setCameraOpen(true)} title={t('Scan order barcode')}>📷</button>
                        <span className="os-search-icon">🔍</span>
                    </div>
                )}
                {!sidebarMode && belowInput}
                {loading && <div className={sidebarMode ? 'os-status' : 'qcp-search-hint'}>{t('Searching…')}</div>}
                {results.length > 0 && (
                    <div className={sidebarMode ? null : 'qcp-results'}>
                        {results.map(o => (
                            sidebarMode ? (
                                <div key={o.order_id} className="os-result" style={{ cursor: 'pointer' }} onClick={() => onSelectOrder(o)}>
                                    <div>
                                        <div className="os-result__name">
                                            {o.customer_name || '—'}
                                            {showBadge && o.__location_name && (
                                                <span className="pkp-order-badge">{o.__location_name}</span>
                                            )}
                                        </div>
                                        <div className="os-result__meta">{o.order_name} · {t(o.items_count === 1 ? '{n} item' : '{n} items', { n: o.items_count })}</div>
                                    </div>
                                    <div className="os-result__arrow">›</div>
                                </div>
                            ) : (
                                <button key={o.order_id} className="qcp-result-row" onClick={() => onSelectOrder(o)}>
                                    <span className="qcp-result-name">{o.order_name}</span>
                                    <span className="qcp-result-customer">
                                        {o.customer_name}
                                        {showBadge && o.__location_name && (
                                            <span className="pkp-order-badge">{o.__location_name}</span>
                                        )}
                                    </span>
                                    <span className="qcp-result-status">{o.fulfillment_status || t('Unfulfilled')}</span>
                                </button>
                            )
                        ))}
                    </div>
                )}
                {!loading && query.length >= 2 && results.length === 0 && (
                    <div className={sidebarMode ? 'os-status' : 'qcp-search-hint'}>{t('No orders found for "{q}"', { q: query })}</div>
                )}
            </div>
        </div>
    );
}

// ── QA Failure Modal ───────────────────────────────────────────────────────────
function QAFailModal({ unit, reasons, onSubmit, onBack }) {
    const t = useT();
    const [step, setStep]     = useState(1);
    const [reason, setReason] = useState('');
    const [savedNote, setSavedNote] = useState('');
    const [noteEditing, setNoteEditing] = useState(false);
    const [draft, setDraft] = useState('');

    function handleSelectReason(r) {
        if (r === reason) return;
        setReason(r);
        setSavedNote('');
        setDraft('');
        setNoteEditing(false);
    }
    function openNoteEditor(e) {
        e.preventDefault();
        e.stopPropagation();
        setDraft(savedNote);
        setNoteEditing(true);
    }
    function saveNote() {
        const trimmed = draft.trim().slice(0, 500);
        setSavedNote(trimmed);
        setNoteEditing(false);
    }
    function cancelNoteEdit() {
        setNoteEditing(false);
        setDraft('');
    }
    function effectiveNote() {
        const v = (noteEditing ? draft : savedNote).trim().slice(0, 500);
        return v || null;
    }
    function notePreview(text) {
        if (!text) return '';
        return text.length > 80 ? text.slice(0, 80) + '…' : text;
    }

    return (
        <div className="qcp-modal-overlay">
            <div className="qcp-modal">
                {step === 1 && (
                    <>
                        <div className="qcp-modal-title">{t('QA Failed —')} {unit.playerName ? `${unit.playerName}${unit.playerNumber ? ` #${unit.playerNumber}` : ''}` : unit.title}</div>
                        <div className="qcp-modal-sub">{t('Select the failure reason')}</div>
                        <div className="qcp-modal-reasons">
                            {reasons.map(r => {
                                const selected = reason === r;
                                const showEditor = selected && noteEditing;
                                const showPreview = selected && !noteEditing && savedNote;
                                return (
                                    <div
                                        key={r}
                                        className={`qcp-modal-reason${selected ? ' qcp-modal-reason--selected' : ''}`}
                                    >
                                        <label className="qcp-modal-reason__row">
                                            <input
                                                type="radio" name="reason" value={r}
                                                checked={selected}
                                                onChange={() => handleSelectReason(r)}
                                            />
                                            <span className="qcp-modal-reason__label">{t(r)}</span>
                                            {selected && !noteEditing && (
                                                <button
                                                    type="button"
                                                    className="qcp-modal-reason__note-pill"
                                                    onClick={openNoteEditor}
                                                >
                                                    {savedNote ? t('Edit Note') : t('Add Note')}
                                                </button>
                                            )}
                                        </label>
                                        {showPreview && (
                                            <div className="qcp-modal-reason__note-preview">
                                                {notePreview(savedNote)}
                                            </div>
                                        )}
                                        {showEditor && (
                                            <div className="qcp-modal-reason__note-edit">
                                                <textarea
                                                    className="qcp-modal-reason__note-textarea"
                                                    value={draft}
                                                    onChange={(e) => setDraft(e.target.value)}
                                                    placeholder={t('Type a note for this QC failure (optional)')}
                                                    rows={3}
                                                    maxLength={500}
                                                    autoFocus
                                                    inputMode="text"
                                                />
                                                <div className="qcp-modal-reason__note-actions">
                                                    <button
                                                        type="button"
                                                        className="qcp-modal-btn qcp-modal-btn--secondary"
                                                        onClick={cancelNoteEdit}
                                                    >
                                                        {t('Cancel')}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="qcp-modal-btn qcp-modal-btn--primary"
                                                        onClick={saveNote}
                                                    >
                                                        {t('Save')}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        <div className="qcp-modal-actions">
                            <button className="qcp-modal-btn qcp-modal-btn--secondary" onClick={onBack}>{t('Cancel')}</button>
                            <button className="qcp-modal-btn qcp-modal-btn--danger" onClick={() => setStep(2)} disabled={!reason}>{t('Next →')}</button>
                        </div>
                    </>
                )}
                {step === 2 && (
                    <>
                        <div className="qcp-modal-title">{unit.playerName ? `${unit.playerName}${unit.playerNumber ? ` #${unit.playerNumber}` : ''}` : unit.title} — {t(reason)}</div>
                        <div className="qcp-modal-sub">{t('What should happen to this item?')}</div>
                        <button className="qcp-modal-action-btn qcp-modal-action-btn--rework" onClick={() => onSubmit(reason, 'rework', effectiveNote())}>
                            <div className="qcp-modal-action-title">🔧 {t('Send for Rework')}</div>
                            <div className="qcp-modal-action-desc">{t('Item needs to be re-pressed. Packing session will be saved and paused.')}</div>
                        </button>
                        <button className="qcp-modal-action-btn qcp-modal-action-btn--partial" onClick={() => onSubmit(reason, 'partial', effectiveNote())}>
                            <div className="qcp-modal-action-title">📦 {t('Partial Fulfillment')}</div>
                            <div className="qcp-modal-action-desc">{t('Exclude this item. Continue packing and fulfill the rest.')}</div>
                        </button>
                        <div className="qcp-modal-actions" style={{marginTop: 12}}>
                            <button className="qcp-modal-btn qcp-modal-btn--secondary" onClick={() => setStep(1)}>{t('← Back')}</button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ── Fulfillment Summary ────────────────────────────────────────────────────────
function FulfillmentSummary({ units, qaState, orderId, orderInfo, session, onDone,
                              totalOpenUnitsAtLocation = 0,
                              fetchFn = authenticatedFetch }) {
    const t = useT();
    const [creating, setCreating] = useState(false);
    const [error, setError]       = useState('');
    const [success, setSuccess]   = useState(false);
    // Intercept modal state (SP-297179 under-scan guard — belt/Layer 1).
    // Shown BEFORE /complete fires when the passed units don't cover every
    // open unit at this location AND the operator didn't explicitly mark any
    // item as fail-partial. The operator must either cancel (go back and
    // finish scanning) or confirm ship-as-partial.
    const [showUnderScanModal, setShowUnderScanModal] = useState(false);

    const passed   = units.filter(u => qaState[u.unitKey]?.result === 'pass');
    const excluded = units.filter(u => qaState[u.unitKey]?.result === 'fail');

    // Count passed units per FO line item ID (handles qty > 1 correctly)
    const passedFoCounts = {};
    for (const u of passed) {
        for (const id of u.foLineItemIds) {
            passedFoCounts[id] = (passedFoCounts[id] || 0) + 1;
        }
    }
    const passedFoLineItems = Object.entries(passedFoCounts).map(([id, quantity]) => ({ id, quantity }));
    const passedUnitCount = passedFoLineItems.reduce((s, li) => s + li.quantity, 0);

    // SP-297179 partial-intent detection.
    //   • hasExplicitPartial — operator failed an item with action='partial'
    //     via the QC fail modal. This is a KNOWN partial; skip the intercept
    //     and send acknowledge_partial=true straight away.
    //   • clientIsPartial — the passed count doesn't cover every open unit at
    //     this location per the FE's view (bundles + base items + embellishments
    //     via getPackingUnits+computeTotalOpenUnitsAtLocation). Used both for
    //     the intercept trigger and as the FE's belief we ship to the server.
    const hasExplicitPartial = units.some(u => {
        const st = qaState[u.unitKey];
        return st?.result === 'fail' && st?.action === 'partial';
    });
    const clientIsPartial = totalOpenUnitsAtLocation > 0
        && passedUnitCount < totalOpenUnitsAtLocation;

    const fulfillmentOrderId = orderInfo?.fulfillmentOrders?.edges
        ?.map(e => e.node)
        ?.find(fo => ['OPEN', 'IN_PROGRESS'].includes(fo.status)
            && fo.assignedLocation?.location?.id === `gid://shopify/Location/${session.shopify_location_id}`)
        ?.id || '';

    // ackOverride=true means the operator has EXPLICITLY confirmed this is a
    // partial fulfillment (via the intercept modal or an explicit fail-partial).
    // Without it, an under-scan will be 409'd by the server so we can surface
    // the modal a second time as a safety net if the intercept was somehow
    // skipped (e.g., stale FE snapshot).
    const doCreateFulfillment = async (ackOverride = false) => {
        setCreating(true); setError('');
        try {
            const acknowledgePartial = ackOverride || hasExplicitPartial;
            const r = await fetchFn(`/api/packing/orders/${orderId}/complete`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    order_name:              orderInfo.name,
                    shopify_location_id:     session.shopify_location_id,
                    portal_userid:           session.presser_id,
                    portal_username:         `${session.first_name} ${session.last_name}`,
                    presser_id:              session.presser_id,
                    passed_fo_line_items:    passedFoLineItems,
                    fulfillment_order_id:    fulfillmentOrderId,
                    // SP-297179 belt & suspenders — FE's belief + view for server cross-check.
                    client_is_partial:       clientIsPartial,
                    client_total_open_units: totalOpenUnitsAtLocation,
                    acknowledge_partial:     acknowledgePartial,
                }),
            });
            const d = await r.json();
            if (!r.ok) {
                // 409 under_scan — server caught a partial the FE didn't. Surface
                // the intercept modal as fallback (rare — normally we intercept
                // first, but a stale FE snapshot can slip through).
                if (r.status === 409 && d?.detail?.code === 'under_scan') {
                    setShowUnderScanModal(true);
                    return;
                }
                const msg = typeof d.detail === 'string' ? d.detail
                    : typeof d?.detail?.message === 'string' ? d.detail.message
                    : Array.isArray(d.detail) ? d.detail.map(e => e.msg || JSON.stringify(e)).join('; ')
                    : t('Fulfillment failed');
                setError(msg);
                return;
            }
            setSuccess(true);
        } catch { setError(t('Network error creating fulfillment')); }
        finally { setCreating(false); }
    };

    const createFulfillment = () => {
        // Layer 1 intercept: if the FE thinks this is a partial and the operator
        // hasn't already flagged it as such via a fail-partial action, force
        // them through the confirmation modal before firing /complete.
        if (clientIsPartial && !hasExplicitPartial) {
            setShowUnderScanModal(true);
            return;
        }
        doCreateFulfillment(false);
    };

    return (
        <>
        <div className="qcp-summary-screen">
            <div className="qcp-header">
                <div className="qcp-header-title">{t('Packing Complete —')} {orderInfo?.name}</div>
            </div>
            <div className="qcp-summary-body">
                <div className="qcp-summary-section qcp-summary-section--passed">
                    <div className="qcp-summary-section-title">✓ {t('Passed QC —')} {passed.length} {passed.length !== 1 ? t('items') : t('item')}</div>
                    {passed.map(u => (
                        <div key={u.unitKey} className="qcp-summary-row">
                            <span className="qcp-summary-check">✓</span>
                            <span>{u.isBundle ? `${t('Bundle')} — ` : ''}{u.title}{u.playerName ? ` — ${u.playerName}${u.playerNumber ? ` #${u.playerNumber}` : ''}` : ''}</span>
                            {u.totalInstances > 1 && <span className="qcp-summary-instance">({u.instanceIndex}/{u.totalInstances})</span>}
                        </div>
                    ))}
                </div>
                {excluded.length > 0 && (
                    <div className="qcp-summary-section qcp-summary-section--excluded">
                        <div className="qcp-summary-section-title">✗ {t('Excluded —')} {excluded.length} {excluded.length !== 1 ? t('items') : t('item')}</div>
                        {excluded.map(u => (
                            <div key={u.unitKey} className="qcp-summary-row">
                                <span className="qcp-summary-x">✗</span>
                                <span>{u.title}{u.playerName ? ` — ${u.playerName}${u.playerNumber ? ` #${u.playerNumber}` : ''}` : ''}</span>
                                <span className="qcp-summary-reason">({t(qaState[u.unitKey]?.reason)})</span>
                            </div>
                        ))}
                    </div>
                )}
                {error && <div className="qcp-error">{error}</div>}
                <button className="qcp-fulfill-btn" onClick={createFulfillment} disabled={creating || passed.length === 0}>
                    {creating ? t('Splitting…') : t('Split Fulfillment — {n} Item{s} Ready →', { n: passed.length, s: passed.length !== 1 ? 's' : '' })}
                </button>
            </div>
        </div>
        {success && (
            <div className="qcp-modal-overlay">
                <div className="qcp-modal">
                    <div className="qcp-modal-title">✓ {t('Fulfillment Split')}</div>
                    <div className="qcp-modal-sub">{t('{n} item{s} moved into a ready-to-ship fulfillment order in Shopify.', { n: passed.length, s: passed.length !== 1 ? 's' : '' })}</div>
                    <div className="qcp-modal-actions" style={{ marginTop: 20 }}>
                        <button className="qcp-modal-btn qcp-modal-btn--primary" onClick={onDone}>
                            {t('Back to Order Search')}
                        </button>
                    </div>
                </div>
            </div>
        )}
        {showUnderScanModal && (
            <div className="qcp-modal-overlay">
                <div className="qcp-modal">
                    <div className="qcp-modal-title">⚠️ {t('Partial Fulfillment?')}</div>
                    <div className="qcp-modal-sub">
                        {t("You've scanned {passed} of {total} open units on this order.", {
                            passed: passedUnitCount,
                            total:  totalOpenUnitsAtLocation,
                        })}
                        <br /><br />
                        {t('Ship these {n} item{s} now as a partial fulfillment, or go back and finish packing the remaining {rem} unit{rs}?', {
                            n:   passedUnitCount,
                            s:   passedUnitCount !== 1 ? 's' : '',
                            rem: totalOpenUnitsAtLocation - passedUnitCount,
                            rs:  (totalOpenUnitsAtLocation - passedUnitCount) !== 1 ? 's' : '',
                        })}
                    </div>
                    <div className="qcp-modal-actions" style={{ marginTop: 20, display: 'flex', gap: 8 }}>
                        <button
                            className="qcp-modal-btn qcp-modal-btn--secondary"
                            onClick={() => { setShowUnderScanModal(false); onDone && onDone(); }}
                            disabled={creating}
                        >
                            {t('← Cancel & keep packing')}
                        </button>
                        <button
                            className="qcp-modal-btn qcp-modal-btn--primary"
                            onClick={() => { setShowUnderScanModal(false); doCreateFulfillment(true); }}
                            disabled={creating}
                        >
                            {creating ? t('Splitting…') : t('Ship as partial →')}
                        </button>
                    </div>
                </div>
            </div>
        )}
        </>
    );
}

// ── Packing Session ────────────────────────────────────────────────────────────
function PackingSession({ orderResult, session, onExit, fetchFn = authenticatedFetch }) {
    const t = useT();
    const order    = orderResult.order;
    const orderId  = order.id?.split('/').pop() || '';
    const savedPackState = orderResult.pack_state || {};

    // SKU Tools: {sku: message} for red badge next to current unit's SKU.
    const [skuNotifications, setSkuNotifications] = useState({});
    useEffect(() => {
        if (!orderId) return;
        fetchFn(`/api/sku-notifications/for-order/${orderId}`)
            .then(r => r.ok ? r.json() : { sku_notifications: {} })
            .then(j => setSkuNotifications(j.sku_notifications || {}))
            .catch(() => setSkuNotifications({}));
    }, [orderId, fetchFn]);

    const allUnits = getPackingUnits(order, session.shopify_location_id);

    // Passed items from a previous session are done — exclude from the queue.
    // Failed items still need re-QC after rework, so they stay in the queue.
    const prevPassedKeys = new Set(
        Object.entries(savedPackState)
            .filter(([, v]) => v.pack_status === 'passed')
            .map(([k]) => k)
    );

    // Only show units still needing QC (not already passed), sorted by product type
    const units = allUnits
        .filter(u => !prevPassedKeys.has(u.group_key))
        .sort((a, b) => (a.productType || '').localeCompare(b.productType || ''));

    const [qaState, setQaState] = useState({});
    const [currentKey, setCurrentKey] = useState(() => units[0]?.unitKey || null);
    const [failModal, setFailModal]   = useState(null);
    const [reasons, setReasons]       = useState([]);
    const [showSummary, setShowSummary] = useState(false);
    const [scanError, setScanError]   = useState('');
    const [cameraOpen, setCameraOpen] = useState(false);

    const currentUnit = units.find(u => u.unitKey === currentKey);
    const doneCount   = Object.keys(qaState).length;
    const allDone     = doneCount === units.length && units.length > 0;

    // Check picking status per unit instance, matching the order detail view logic.
    // A unit is "picked" only when ALL SKUs (parent + children) are picked for this instance.
    // If no picking has been done for the entire order, treat as picked (show normal buttons).
    const anyPickingDone = Object.values(savedPackState).some(s => (s.qty_picked || 0) > 0);
    const isUnitPicked = (unit) => {
        if (!anyPickingDone) return true;
        const skuQtyMap = unit.skuQtyMap || {};
        const skus = Object.keys(skuQtyMap);
        if (skus.length === 0) return true;
        for (const sku of skus) {
            const st = savedPackState[sku];
            if (!st || !(st.qty_total > 0)) return false;
            const bundleQty = skuQtyMap[sku] || 1;
            const offset = (unit.instanceIndex - 1) * bundleQty;
            const picked = Math.min(Math.max(0, (st.qty_picked || 0) - offset), bundleQty);
            if (picked < bundleQty) return false;
        }
        return true;
    };

    useEffect(() => {
        fetchFn('/api/packing/settings')
            .then(r => r.json())
            .then(d => setReasons((d.qa_fail_reasons || []).filter(r => !r.archived).map(r => r.text ?? r)))
            .catch(() => {});
    }, [fetchFn]);

    useEffect(() => {
        if (allDone) setShowSummary(true);
    }, [allDone]);

    const handleScan = useCallback((code) => {
        setScanError('');
        const match = units.find(u =>
            !qaState[u.unitKey] && u.barcodes.some(b => b === code || b.endsWith(code))
        );
        if (match) {
            setCurrentKey(match.unitKey);
        } else {
            setScanError(t('No unscanned item found for barcode: {code}', { code }));
            setTimeout(() => setScanError(''), 3000);
        }
    }, [units, qaState]);

    useBarcodeScanner(handleScan);

    const recordQA = useCallback(async (unit, result, reason = null, action = null, note = null) => {
        const newQaState = { ...qaState, [unit.unitKey]: { result, reason, action, note } };
        setQaState(newQaState);

        await fetchFn(`/api/packing/orders/${orderId}/qa`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                group_key:           unit.group_key,
                instance_index:      unit.instanceIndex,
                result,
                fail_reason:         reason,
                fail_action:         action,
                fail_note:           note,
                order_name:          order.name,
                shopify_location_id: session.shopify_location_id,
                portal_userid:       session.presser_id,
                portal_username:     `${session.first_name} ${session.last_name}`,
            }),
        }).catch(() => {});

        if (result === 'pass') playSuccess(); else playError();
        setFailModal(null);
        const nextUnit = units.find(u => !newQaState[u.unitKey]);
        setCurrentKey(nextUnit?.unitKey || null);
    }, [qaState, units, orderId, order.name, session, onExit]);

    if (showSummary) {
        // SP-297179 under-scan guard — provide the FE's belief about how many
        // units are actually open at this location so FulfillmentSummary can
        // intercept before firing /complete when the pack looks partial.
        return <FulfillmentSummary
            units={units}
            qaState={qaState}
            orderId={orderId}
            orderInfo={order}
            session={session}
            totalOpenUnitsAtLocation={computeTotalOpenUnitsAtLocation(order, session.shopify_location_id)}
            onDone={() => onExit('complete')}
            fetchFn={fetchFn}
        />;
    }

    if (units.length === 0) {
        return (
            <div className="qcp-session-screen">
                <div className="qcp-header">
                    <button className="qcp-back-btn" onClick={() => onExit('back')}>{t('← Back')}</button>
                    <div className="qcp-header-title">📦 {order.name}</div>
                </div>
                <div className="qcp-all-done">
                    <div className="qcp-all-done-icon">✓</div>
                    <div className="qcp-all-done-title">{t('All items packed')}</div>
                    <div className="qcp-all-done-sub">{t('Every item in this order has already passed QC. Use Reset Packing History on the order page to start over.')}</div>
                    <button className="qcp-back-btn" style={{ marginTop: 16 }} onClick={() => onExit('back')}>{t('Back to Order Search')}</button>
                </div>
            </div>
        );
    }

    // Print rework slip button: visible whenever the order has ≥1 failed
    // pack instance — either from a prior session (savedPackState row's
    // pack_status='failed') or from this in-progress session's qaState.
    // Hits the same single-order endpoint the QC Board uses.
    const hasFails =
        Object.values(savedPackState || {}).some(s => s?.pack_status === 'failed') ||
        Object.values(qaState).some(s => s?.result === 'fail');
    const reworkPrintFoId = (() => {
        const edges = (order?.fulfillmentOrders?.edges || []).map(e => e.node);
        const match = edges.find(fo =>
            ((fo?.assignedLocation?.location?.id) || '').split('/').pop() === session.shopify_location_id
        );
        return (match || edges[0])?.id || '';
    })();
    const onPrintRework = () => {
        if (!orderId || !reworkPrintFoId) return;
        const presserName = [session.first_name, session.last_name].filter(Boolean).join(' ').trim();
        const params = new URLSearchParams({
            store: session.store_key,
            fulfillment_order_id: reworkPrintFoId,
        });
        // Override the QC attribution with the PIN user actually printing the
        // slip. Without this, the slip credits the admin JWT (kiosk owner /
        // tab session) instead of the packer who tapped Print.
        if (presserName) params.set('qc_name', presserName);
        if (session.presser_id) params.set('portal_userid', String(session.presser_id));
        window.open(`/api/orders/${orderId}/rework-slip-html?${params.toString()}`, '_blank', 'noopener');
    };

    return (
        <div className="qcp-session-screen">
            <div className="qcp-header">
                <button className="qcp-back-btn" onClick={() => onExit('back')}>{t('← Back')}</button>
                <div className="qcp-header-title">📦 {order.name}</div>
                <div className="qcp-header-progress">{doneCount} / {units.length} {t('done')}</div>
            </div>

            {hasFails && (
                <button
                    type="button"
                    className="qcp-rework-print-btn"
                    onClick={onPrintRework}
                    title={t('Print rework slip for this order')}
                >
                    🖨 {t('Print Rework Slip')}
                </button>
            )}

            {cameraOpen && (
                <CameraScanner
                    onDetect={(barcode) => { setCameraOpen(false); handleScan(barcode); }}
                    onClose={() => setCameraOpen(false)}
                />
            )}

            <div className="qcp-top-fixed">
            <div className="qcp-scan-row">
                <input
                    className="qcp-scan-input"
                    placeholder={t('Scan barcode…')}
                    readOnly
                    value=""
                />
                <button className="pkp-camera-btn" onClick={() => setCameraOpen(true)} title={t('Open camera scanner')}>📷</button>
                {scanError && <div className="qcp-scan-error">{scanError}</div>}
            </div>

            {currentUnit ? (
                <div className="qcp-current-card">
                    {/* Row 1: main image (left) | player name/number/variant (right) */}
                    <div className="qcp-current-top">
                        <div className="qcp-current-images">
                            {(currentUnit.previewFront || currentUnit.previewBack) ? (
                                <>
                                    {currentUnit.previewFront && (
                                        <div className="qcp-current-image-item">
                                            <SmartProductImage src={currentUnit.previewFront} size={800} alt={t('Front')} className="qcp-current-image-img" />
                                            <div className="qcp-current-image-label">{t('Front')}</div>
                                        </div>
                                    )}
                                    {currentUnit.previewBack && (
                                        <div className="qcp-current-image-item">
                                            <SmartProductImage src={currentUnit.previewBack} size={800} alt={t('Back side')} className="qcp-current-image-img" />
                                            <div className="qcp-current-image-label">{t('Back side')}</div>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="qcp-current-image-item">
                                    {currentUnit.imageUrl
                                        ? <SmartProductImage src={currentUnit.imageUrl} alt={currentUnit.title} className="qcp-current-image-img" />
                                        : <div className="qcp-current-img-placeholder">📦</div>
                                    }
                                </div>
                            )}
                        </div>
                        <div className="qcp-current-player-block">
                            {currentUnit.playerName && <div className="qcp-current-player-name">{currentUnit.playerName}</div>}
                            {currentUnit.playerNumber && <div className="qcp-current-player-number">#{currentUnit.playerNumber}</div>}
                            {currentUnit.variantTitle && <div className="qcp-current-variant">{currentUnit.variantTitle}</div>}
                        </div>
                    </div>
                    {/* Row 2: bundled product images spanning full width */}
                    {currentUnit.childImages?.length > 0 && (
                        <div className="qcp-current-embellishments">
                            {currentUnit.childImages.map((c, i) => (
                                <SmartProductImage key={i} src={c.url} alt={c.title} className="qcp-current-embellishment-img" />
                            ))}
                        </div>
                    )}
                    {/* Row 3: product name + SKU */}
                    <div className="qcp-current-meta">
                        <div className="qcp-current-title">{currentUnit.title}</div>
                        {currentUnit.sku && <div className="qcp-current-sku">{currentUnit.sku}</div>}
                        {currentUnit.sku && skuNotifications[currentUnit.sku] && (
                            <div style={{
                                display: 'inline-block',
                                marginTop: 8,
                                padding: '7px 16px',
                                borderRadius: 16,
                                background: '#fde8e8',
                                color: '#c00',
                                fontWeight: 700,
                                fontSize: '1.15em',
                                border: '2px solid #fbb',
                            }}>
                                {skuNotifications[currentUnit.sku]}
                            </div>
                        )}
                        {currentUnit.totalInstances > 1 && (
                            <div className="qcp-current-instance">{t('Unit')} {currentUnit.instanceIndex} {t('of')} {currentUnit.totalInstances}</div>
                        )}
                    </div>
                    {/* Row 4: Pass/Fail full width */}
                    <div className="qcp-current-actions">
                        {isUnitPicked(currentUnit) ? (
                            <>
                                <button className="qcp-pass-btn" onClick={() => recordQA(currentUnit, 'pass')}>✓ {t('Pass')}</button>
                                <button className="qcp-fail-btn" onClick={() => setFailModal(currentUnit)}>✗ {t('Fail')}</button>
                            </>
                        ) : (
                            <>
                                <button className="qcp-warn-btn" onClick={() => recordQA(currentUnit, 'pass')}>{t('Not Picked & Pass')}</button>
                                <button className="qcp-fail-btn" onClick={() => setFailModal(currentUnit)}>{t('Not Picked & Fail')}</button>
                            </>
                        )}
                    </div>
                </div>
            ) : (
                <div className="qcp-no-current">{t('Scan a barcode or select an item from the queue below')}</div>
            )}
            </div>{/* end qcp-top-fixed */}

            <div className="qcp-queue">
                {units.map(u => {
                    const state = qaState[u.unitKey];
                    const isCurrent = u.unitKey === currentKey;
                    return (
                        <button
                            key={u.unitKey}
                            className={`qcp-queue-row${isCurrent ? ' qcp-queue-row--current' : ''}${state?.result === 'pass' ? ' qcp-queue-row--pass' : ''}${state?.result === 'fail' ? ' qcp-queue-row--fail' : ''}${state ? ' qcp-queue-row--done' : ''}`}
                            onClick={() => { if (!isCurrent) setCurrentKey(u.unitKey); }}
                        >
                            <div className="qcp-queue-img-wrap">
                                {u.imageUrl
                                    ? <SmartProductImage src={u.imageUrl} alt={u.title} className="qcp-queue-img" />
                                    : <div className="qcp-queue-img-placeholder">📦</div>
                                }
                            </div>
                            <div className="qcp-queue-info">
                                <div className="qcp-queue-title-text">{u.isBundle ? `${t('Bundle')} — ` : ''}{u.title}</div>
                                {(u.playerName || u.playerNumber) && (
                                    <div className="qcp-queue-player">
                                        {u.playerName && <span className="qcp-queue-player-name">{u.playerName}</span>}
                                        {u.playerNumber && <span className="qcp-queue-player-number">#{u.playerNumber}</span>}
                                    </div>
                                )}
                                {u.totalInstances > 1 && <div className="qcp-queue-instance">{t('Unit')} {u.instanceIndex}/{u.totalInstances}</div>}
                                {u.sku && skuNotifications[u.sku] && (
                                    <div style={{
                                        display: 'inline-block',
                                        marginTop: 6,
                                        padding: '5px 12px',
                                        borderRadius: 14,
                                        background: '#fde8e8',
                                        color: '#c00',
                                        fontWeight: 700,
                                        fontSize: '1em',
                                        border: '1px solid #fbb',
                                    }}>
                                        {skuNotifications[u.sku]}
                                    </div>
                                )}
                            </div>
                            <div className="qcp-queue-status">
                                {state?.result === 'pass' && <span className="qcp-status-pass">✓</span>}
                                {state?.result === 'fail' && <span className="qcp-status-fail">✗</span>}
                            </div>
                        </button>
                    );
                })}
            </div>

            {failModal && (
                <QAFailModal
                    unit={failModal}
                    reasons={reasons}
                    onSubmit={(reason, action, note) => recordQA(failModal, 'fail', reason, action, note)}
                    onBack={() => setFailModal(null)}
                />
            )}
        </div>
    );
}

// ── Main export ────────────────────────────────────────────────────────────────
export default function QCPortal({ fullscreen }) {
    const t = useT();
    const { user, emulating, startEmulation }             = useAuth();
    const { activeStore }                 = useStore();
    const { orderId: urlOrderId }         = useParams();
    const location                        = useLocation();
    const navigate                        = useNavigate();
    const returnTo                        = location.state?.returnTo || null;
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [orderDetail, setOrderDetail]   = useState(null);
    const [loadingOrder, setLoadingOrder] = useState(false);
    const [selectedOrderContext, setSelectedOrderContext] = useState(null); // { orderShopifyLocationId, orderStoreKey }
    const [bootstrapError, setBootstrapError] = useState('');
    // 2026-05-19: session_start and _end are now written via independent POSTs
    // (session-open / session-close), not deferred to close-gesture time. The
    // ref tracks the currently-open order so pagehide / idle-lock / sign-out
    // paths can still fire a close.
    const sessionOpenForRef = useRef(null);  // { orderId, storeKey, sloc }
    // Location picker: pending order row awaiting location choice
    const [pendingLocOrderResult, setPendingLocOrderResult] = useState(null);
    const [pendingLocCandidates, setPendingLocCandidates] = useState([]);
    const [pendingNotifications, setPendingNotifications] = useState([]);
    // 2026-09-02: full list of the order's broadcast (order) notifications, kept
    // separately from the auto-shown queue so the "Review notification" button
    // in the Working-at banner can re-show the on-open popup at any time.
    const [orderNotifications, setOrderNotifications] = useState([]);

    // Ack once per (store, order, notif) — sessionStorage-backed so it survives
    // the exit-then-reopen round-trip after a label print (which unmounts and
    // remounts the order via selectOrder). Without this, every re-fetch of
    // /api/packing/orders/{id} would re-populate active_notifications and
    // re-prompt the operator for the same notification they already ack'd.
    // sessionStorage (not localStorage) keeps the ack tied to this browser
    // tab's session — a totally fresh workday still gets the prompt once.
    const ACK_STORAGE_KEY = 'packing_acked_notifs_v1';
    const _loadAckedSet = () => {
        try {
            const raw = sessionStorage.getItem(ACK_STORAGE_KEY);
            const arr = raw ? JSON.parse(raw) : [];
            return new Set(Array.isArray(arr) ? arr : []);
        } catch { return new Set(); }
    };
    const _saveAckedSet = (set) => {
        try { sessionStorage.setItem(ACK_STORAGE_KEY, JSON.stringify([...set])); } catch {}
    };
    const _ackKey = (n, storeKey, orderId) =>
        `${storeKey || ''}:${orderId || ''}:${n?.id ?? ''}`;
    const _ackedNotifsRef = useRef(_loadAckedSet());
    const _filterUnacked = (list, storeKey, orderId) => {
        const acked = _ackedNotifsRef.current;
        return (list || []).filter(n => !acked.has(_ackKey(n, storeKey, orderId)));
    };
    const _rememberAcked = (list, storeKey, orderId) => {
        const acked = _ackedNotifsRef.current;
        for (const n of (list || [])) acked.add(_ackKey(n, storeKey, orderId));
        _saveAckedSet(acked);
    };

    // Fullscreen: session comes from the shared portal PIN login (presser_session key).
    // Sidebar:    build a session from the logged-in admin's profile.
    const presserSession = fullscreen ? getPresserSession() : null;

    // Trap browser back button in fullscreen portal to avoid redirect loops.
    useEffect(() => {
        if (!fullscreen) return;
        window.history.pushState(null, '', window.location.href);
        const trap = () => window.history.pushState(null, '', window.location.href);
        window.addEventListener('popstate', trap);
        return () => window.removeEventListener('popstate', trap);
    }, [fullscreen]);

    // Idle lock: 15 min of inactivity in fullscreen portal → PIN screen.
    // Fire session-close for the open order so its end is bracketed before
    // the PIN session clears.
    useIdleLock(fullscreen && !!presserSession, IDLE_LOCK_MS, () => {
        const cur = sessionOpenForRef.current;
        if (cur) {
            const sess = getSession();
            try {
                fetchFn(`/api/packing/orders/${cur.orderId}/session-close`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        portal_userid:       sess?.presser_id || null,
                        portal_username:     sess
                            ? `${sess.first_name || ''} ${sess.last_name || ''}`.trim()
                            : '',
                        shopify_location_id: cur.sloc || '',
                    }),
                    keepalive: true,
                }, cur.storeKey).catch(() => {});
            } catch {}
        }
        clearPressingSession();
        const kiosk = localStorage.getItem('ops_portal_token');
        if (kiosk) sessionStorage.setItem('pressing_admin_token', kiosk);
        window.location.href = '/pressing-portal/login';
    });

    // Multi-location assignments (PIN-only session)
    const assignments = useMemo(() => presserSession?.assignments || [], [presserSession]);
    const locationMap = useMemo(() => {
        const m = new Map();
        for (const a of assignments) {
            m.set(String(a.shopify_location_id), { store_key: a.store_key, location_name: a.location_name });
        }
        return m;
    }, [assignments]);

    // Default store (for admin sidebar + single-assignment sessions)
    const defaultStoreKey = presserSession
        ? (assignments[0]?.store_key || activeStore || 'soccerpost')
        : (activeStore || 'soccerpost');
    const baseFetchFn = fullscreen ? pressingFetch : authenticatedFetch;
    // fetchFn signature: (url, opts, storeKeyOverride = null)
    const fetchFn = useCallback((url, opts, storeKeyOverride = null) => {
        const sk = storeKeyOverride || defaultStoreKey;
        const fullUrl = url.includes('?store=') || url.includes('&store=')
            ? url
            : `${url}${url.includes('?') ? '&' : '?'}store=${sk}`;
        return baseFetchFn(fullUrl, opts);
    }, [baseFetchFn, defaultStoreKey]);

    // Legacy fallback location (admin sidebar / single-assignment presser)
    const legacyShopifyLocationId = presserSession
        ? (assignments.length === 1 ? assignments[0].shopify_location_id : '')
        : (emulating?.shopify_location_id || user?.shopify_location_id || '');

    // Header session — location_name is derived for header display only
    let headerLocationName = '';
    if (presserSession) {
        if (assignments.length === 1) headerLocationName = assignments[0].location_name || '';
        else if (assignments.length > 1) headerLocationName = t('Multiple locations ({n})', { n: assignments.length });
        else headerLocationName = presserSession.location_name || '';
    } else {
        headerLocationName = emulating?.name || user?.location_name || '';
    }

    const baseSession = fullscreen
        ? (presserSession ? { ...presserSession, shopify_location_id: legacyShopifyLocationId, location_name: headerLocationName, store_key: presserSession.store_key || defaultStoreKey } : null)
        : {
            portal_type:         'packing',
            shopify_location_id: legacyShopifyLocationId,
            location_name:       headerLocationName,
            first_name:          user?.user || '',
            last_name:           '',
            presser_id:          null,
            store_key:           defaultStoreKey,
          };

    // Fullscreen guard: redirect to portal login if no valid packing session
    useEffect(() => {
        if (fullscreen && (!presserSession || !(presserSession.portal_type === 'packing' || (presserSession.roles || []).includes('packing')))) {
            window.location.href = '/pressing-portal/login';
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Auto-fullscreen on launch — iPad/Android kiosk only. Desktop Chrome
    // runs the portal as a normal tab so label/slip print dialogs don't force
    // a fullscreen-exit round-trip.
    useEffect(() => {
        const isDesktop = isDesktopBrowser();   // 2026-10-14: iPadOS 27 no longer reports maxTouchPoints > 1 (constants/deviceDetect.js)
        if (isDesktop) return;
        const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
        if (!fullscreen || isStandalone) return;
        if (document.fullscreenElement) return;
        if (document.documentElement.requestFullscreen) {
            document.documentElement.requestFullscreen().catch(() => {});
        }
    }, [fullscreen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Resolve per-order context (shopify_location_id + store_key) from the order row's tags.
    // Admin/sidebar mode falls back to the legacy single-location.
    const resolveOrderContext = useCallback((orderResult) => {
        if (presserSession && assignments.length > 0) {
            const locId = orderResult.shopify_location_id || assignments[0]?.shopify_location_id || '';
            const sk    = orderResult.__store_key || assignments[0]?.store_key || defaultStoreKey;
            return { orderShopifyLocationId: String(locId), orderStoreKey: sk };
        }
        return {
            orderShopifyLocationId: legacyShopifyLocationId,
            orderStoreKey:          activeStore || 'soccerpost',
        };
    }, [presserSession, assignments, legacyShopifyLocationId, activeStore, defaultStoreKey]);

    // Admin-mode resolver: mirror PickingPortal.resolveAdminOrderLoc.
    // Fetches the order's cache-locations. Single-loc → auto-select silently
    // (mirroring sidebar via startEmulation). Multi-loc → open the shared
    // pending-loc modal (pendingLocOrderResult / pendingLocCandidates).
    // Returns the resolved ctx synchronously when auto-selected, or null
    // when the picker was opened.
    const resolveAdminOrderCtx = useCallback(async (orderResult) => {
        const orderId  = orderResult.order_id;
        const storeKey = orderResult.__store_key || activeStore || 'soccerpost';
        let locs = [];
        try {
            const r = await authenticatedFetch(`/api/orders/${orderId}/cache-locations?store=${storeKey}`);
            if (r.ok) {
                const j = await r.json();
                locs = (j.locations || []).filter(l => l && l.shopify_location_id);
            }
        } catch { /* fall through */ }
        const _commit = (loc) => {
            try {
                startEmulation?.({
                    id:                  loc.id ?? null,
                    name:                loc.name || loc.location_name || '',
                    shopify_location_id: loc.shopify_location_id,
                    is_franchise:        !!loc.is_franchise,
                    store_key:           storeKey,
                });
            } catch { /* non-fatal */ }
            return { orderShopifyLocationId: loc.shopify_location_id, orderStoreKey: storeKey };
        };
        // Honor ?loc=<sid> when it names a loc the order actually has.
        const urlLoc = new URLSearchParams(window.location.search).get('loc') || '';
        if (urlLoc && urlLoc !== 'all') {
            const hit = locs.find(l => String(l.shopify_location_id) === String(urlLoc));
            if (hit) return _commit(hit);
        }
        if (legacyShopifyLocationId) {
            const hit = locs.find(l => String(l.shopify_location_id) === String(legacyShopifyLocationId));
            if (hit) return _commit(hit);
        }
        if (locs.length === 1) return _commit(locs[0]);
        if (locs.length > 1) {
            setPendingLocOrderResult({ orderResult, baseCtx: { orderStoreKey: storeKey } });
            setPendingLocCandidates(locs.map(l => ({
                id:                  l.id ?? null,
                shopify_location_id: l.shopify_location_id,
                location_name:       l.name || l.location_name || l.shopify_location_id,
                is_franchise:        !!l.is_franchise,
                store_key:           storeKey,
            })));
            return null;
        }
        return { orderShopifyLocationId: legacyShopifyLocationId || '', orderStoreKey: storeKey };
    }, [activeStore, legacyShopifyLocationId, startEmulation]);

    const loadOrderWithCtx = useCallback((orderResult, ctx) => {
        // Session handling under the open-now/close-now model:
        //  - Switching to a different order: fire session-close for the prior
        //    order, then session-open for the new one.
        //  - Same order being re-loaded (post-save refresh, deep-link round
        //    trip): backend session-open is idempotent — if an open start
        //    already exists for (presser, order), it no-ops. Safe to call.
        const prevOrderId = selectedOrder?.order_id;
        const isSameOrder = prevOrderId === orderResult.order_id;
        const sess = getSession();
        const sessUserid   = sess?.presser_id || null;
        const sessUsername = sess ? `${sess.first_name || ''} ${sess.last_name || ''}`.trim() : (user?.user || '');
        if (!isSameOrder && selectedOrder && selectedOrderContext) {
            try {
                fetchFn(`/api/packing/orders/${selectedOrder.order_id}/session-close`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        portal_userid:       sessUserid,
                        portal_username:     sessUsername,
                        shopify_location_id: selectedOrderContext.orderShopifyLocationId || '',
                    }),
                    keepalive: true,
                }, selectedOrderContext.orderStoreKey).catch(() => {});
            } catch {}
        }
        // Fire session-open for the new (or re-opened) order. Backend is
        // idempotent on (presser, order); duplicate opens no-op.
        try {
            fetchFn(`/api/packing/orders/${orderResult.order_id}/session-open`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    portal_userid:       sessUserid,
                    portal_username:     sessUsername,
                    shopify_location_id: ctx.orderShopifyLocationId || '',
                }),
            }, ctx.orderStoreKey).catch(() => {});
            sessionOpenForRef.current = {
                orderId:  orderResult.order_id,
                storeKey: ctx.orderStoreKey,
                sloc:     ctx.orderShopifyLocationId || '',
            };
        } catch {}
        setSelectedOrderContext(ctx);
        setLoadingOrder(true);
        fetchFn(
            `/api/packing/orders/${orderResult.order_id}` +
            (ctx.orderShopifyLocationId ? `?shopify_location_id=${encodeURIComponent(ctx.orderShopifyLocationId)}` : ''),
            undefined,
            ctx.orderStoreKey,
        )
        .then(r => r.json())
        .then(d => {
            setOrderDetail(d);
            setSelectedOrder(orderResult);
            const _orderName = d?.order?.name || d?.order?.order_name || null;
            const _pending = [
                ...synthesizeJumbledBundleNotifications(d?.jumbled_bundles, _orderName),
                ...(Array.isArray(d?.active_notifications) ? d.active_notifications : []),
            ];
            // Filter out anything already acknowledged this session for this
            // (store, order). Fresh notifications (e.g. a manager broadcasts
            // one mid-pack) still get through since their id won't be in the
            // acked set.
            const _unacked = _filterUnacked(
                _pending, ctx.orderStoreKey, orderResult.order_id,
            );
            setOrderNotifications(_pending);
            if (_unacked.length) setPendingNotifications(_unacked);
        })
        .catch(() => {})
        .finally(() => setLoadingOrder(false));
    }, [fetchFn, selectedOrder, selectedOrderContext, user]);

    // Fire session-close for the current packing order. Backend writes
    // _session_end at NOW(); no-op if no open start exists for this
    // (presser, order). Idempotent.
    const fireSessionClose = useCallback(() => {
        const ord       = selectedOrder;
        const ctx       = selectedOrderContext;
        if (!ord || !ctx) return;
        sessionOpenForRef.current = null;
        const sess = getSession();
        try {
            fetchFn(`/api/packing/orders/${ord.order_id}/session-close`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    portal_userid:       sess?.presser_id || null,
                    portal_username:     sess
                        ? `${sess.first_name || ''} ${sess.last_name || ''}`.trim()
                        : (user?.user || ''),
                    shopify_location_id: ctx.orderShopifyLocationId || '',
                }),
                keepalive: true,
            }, ctx.orderStoreKey).catch(() => {});
        } catch {}
    }, [fetchFn, selectedOrder, selectedOrderContext, user]);

    // pagehide / visibilitychange='hidden' flush session-close on tab close,
    // iPad swipe-away, app force-quit. `keepalive: true` lets the browser
    // ship the request during teardown.
    useEffect(() => {
        const flushClose = () => {
            const cur = sessionOpenForRef.current;
            if (!cur) return;
            const sess = getSession();
            try {
                fetchFn(`/api/packing/orders/${cur.orderId}/session-close`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        portal_userid:       sess?.presser_id || null,
                        portal_username:     sess
                            ? `${sess.first_name || ''} ${sess.last_name || ''}`.trim()
                            : (user?.user || ''),
                        shopify_location_id: cur.sloc || '',
                    }),
                    keepalive: true,
                }, cur.storeKey).catch(() => {});
            } catch {}
        };
        const onVisibility = () => {
            if (document.visibilityState === 'hidden') flushClose();
        };
        window.addEventListener('pagehide', flushClose);
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.removeEventListener('pagehide', flushClose);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [fetchFn, user]);

    const selectOrder = useCallback(async (orderResult) => {
        // Admin sidebar: resolve the order's actual loc via cache-locations.
        // Auto-select single-loc silently (mirroring to sidebar via
        // startEmulation); prompt on multi-loc. Never fall back to an empty
        // shopify_location_id (pre-fix bug that let pack events land loc-less).
        if (!presserSession) {
            const adminCtx = await resolveAdminOrderCtx(orderResult);
            if (adminCtx == null) return; // picker now open; onPick will commit
            loadOrderWithCtx(orderResult, adminCtx);
            return;
        }
        let ctx = resolveOrderContext(orderResult);

        // Multi-location check: only show the picker when the ORDER itself has
        // work at more than one of the user's assigned locations. The packing
        // search returns one row per (order, location) pair, so when an order
        // only has open FOs at a single one of the user's locations, the row
        // already carries `shopify_location_id` and we can auto-pick it. The
        // old logic fired the picker any time the user had >1 assignments in
        // the same store — even for orders that only existed at one location —
        // forcing them to choose between locations that don't apply.
        if (presserSession && assignments.length > 1) {
            const orderStoreKey = ctx.orderStoreKey;
            const storeCandidates = assignments.filter(a => a.store_key === orderStoreKey);
            // Narrow to the locations where this order actually has FO work,
            // when the search row tells us. orderResult.fo_locations (or the
            // multi-loc array) is set by the backend when the order has open
            // FOs at multiple of the user's assignments.
            let orderLocs = (orderResult.fo_locations
                          || orderResult.shopify_location_ids
                          || []).map(String);
            // Fallback: query the cache-locations endpoint when the search row
            // didn't carry an explicit list. Without this, an URL deeplink or a
            // row missing fo_locations falls through to "all storeCandidates"
            // and forces the picker even on single-location orders.
            if (orderLocs.length === 0) {
                try {
                    const r = await fetchFn(`/api/orders/${orderResult.order_id}/cache-locations`, undefined, orderStoreKey);
                    if (r.ok) {
                        const j = await r.json();
                        orderLocs = (j.locations || []).map(l => String(l.shopify_location_id));
                    }
                } catch {}
            }
            const matched = orderLocs.length
                ? storeCandidates.filter(a => orderLocs.includes(String(a.shopify_location_id)))
                : storeCandidates;
            // Only ask the user to pick when there's genuine ambiguity —
            // i.e. ≥2 of their assigned locations have open work on this
            // order. A single row carrying its own shopify_location_id wins.
            const ambiguous = matched.length > 1
                            && !orderResult.shopify_location_id;
            if (ambiguous) {
                setPendingLocOrderResult({ orderResult, baseCtx: ctx });
                setPendingLocCandidates(matched);
                return;
            }
            // Single-location auto-resolve: if exactly one of the user's
            // assigned locations has the order, skip the picker entirely and
            // hard-set that as the order's location.
            if (matched.length === 1 && !orderResult.shopify_location_id) {
                ctx = { ...ctx, orderShopifyLocationId: matched[0].shopify_location_id };
            }
        }

        loadOrderWithCtx(orderResult, ctx);
    }, [resolveOrderContext, loadOrderWithCtx, presserSession, assignments]);

    // Bootstrap: load order from URL param (sidebar "View in" deep link or fullscreen bookmark).
    // Multi-assignment sessions normally can't pre-resolve store/location from
    // the URL — but when QCActionBar's "Close Label" navigation includes
    // ?store=<sk>&loc=<locId> in the return URL, we have everything needed to
    // restore the same order without bouncing through the search screen.
    // Ref-based gate for the URL-first mount (label-close return-nav, deep
    // links). Starts false; the bootstrap useEffect below flips it true when
    // it runs. Read by the pre-search render gate to suppress a first-frame
    // flash of the search list while the bootstrap effect + async selectOrder
    // are still pending state (loadingOrder=true hasn't landed yet).
    const bootstrapFiredRef = useRef(false);
    useEffect(() => {
        // Mark fired REGARDLESS of the path taken below. Once this ref is
        // true, the render gate falls through to the normal state-driven
        // flow (loadingOrder → search → order-detail), so nothing gets
        // permanently stuck at "Loading order…".
        bootstrapFiredRef.current = true;
        if (!urlOrderId) return;
        const params = new URLSearchParams(window.location.search);
        const urlStoreKey = params.get('store') || '';
        const urlLocId    = params.get('loc') || '';
        if (presserSession && assignments.length > 1 && !(urlStoreKey && urlLocId)) {
            setBootstrapError(t('Please open this order from the search page.'));
            return;
        }
        const orderResult = { order_id: urlOrderId };
        if (urlStoreKey) orderResult.__store_key = urlStoreKey;
        if (urlLocId)    orderResult.shopify_location_id = urlLocId;
        selectOrder(orderResult);
        // Strip the query params so a refresh / nav-share doesn't keep them
        // around (and so the URL stays clean once the order is restored).
        if (urlStoreKey || urlLocId) {
            const url = new URL(window.location.href);
            url.searchParams.delete('store');
            url.searchParams.delete('loc');
            window.history.replaceState(null, '', url.toString());
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const exitSession = useCallback(() => {
        // Fire session-close BEFORE clearing the order/context state, so
        // fireSessionClose can read the current order_id + store_key. All
        // packing close gestures route through here via QCBoard's
        // onDone(action) → saveState().then(onExit()).
        fireSessionClose();
        setSelectedOrder(null);
        setOrderDetail(null);
        setOrderNotifications([]);
        setSelectedOrderContext(null);
        if (returnTo) navigate(returnTo);
    }, [returnTo, navigate, fireSessionClose]);

    // Render nothing while redirecting
    if (fullscreen && (!presserSession || !(presserSession.portal_type === 'packing' || (presserSession.roles || []).includes('packing')))) {
        return null;
    }

    // "Working at" location name — shown when user explicitly picked a location for this order.
    const orderLocationName = (() => {
        if (!selectedOrderContext) return null;
        if (!presserSession || assignments.length <= 1) return null;
        const locId = selectedOrderContext.orderShopifyLocationId;
        if (!locId) return null;
        const a = assignments.find(a => String(a.shopify_location_id) === String(locId));
        return a?.location_name || null;
    })();

    // Location picker modal — shown when order is pending location selection.
    const locPickerModal = pendingLocOrderResult && pendingLocCandidates.length > 1 ? (
        <LocationPickerModal
            candidates={pendingLocCandidates}
            onPick={(loc) => {
                const { orderResult, baseCtx } = pendingLocOrderResult;
                setPendingLocOrderResult(null);
                setPendingLocCandidates([]);
                // Admin-mode picker path: mirror the chosen loc to the
                // sidebar dropdown so the top-right chip flips from
                // "All Locations" to the actual location.
                if (!presserSession) {
                    try {
                        startEmulation?.({
                            id:                  loc.id ?? null,
                            name:                loc.location_name || '',
                            shopify_location_id: loc.shopify_location_id,
                            is_franchise:        !!loc.is_franchise,
                            store_key:           baseCtx.orderStoreKey || activeStore || 'soccerpost',
                        });
                    } catch { /* non-fatal */ }
                }
                loadOrderWithCtx(orderResult, {
                    ...baseCtx,
                    orderShopifyLocationId: loc.shopify_location_id,
                });
            }}
            onCancel={() => { setPendingLocOrderResult(null); setPendingLocCandidates([]); }}
        />
    ) : null;

    if (loadingOrder) return <>{locPickerModal}<div className="qcp-loading">{t('Loading order…')}</div></>;
    if (selectedOrder && orderDetail && selectedOrderContext) {
        // Build a per-order effective session by overlaying this order's location.
        // If the context location is empty/missing (e.g. admin sidebar deep link),
        // derive it from the order's first OPEN fulfillment order.
        let effectiveLocId = selectedOrderContext.orderShopifyLocationId;
        if (!effectiveLocId && orderDetail.order) {
            const fos = (orderDetail.order.fulfillmentOrders?.edges || []).map(e => e.node);
            const openFo = fos.find(fo => fo.status === 'OPEN' || fo.status === 'IN_PROGRESS');
            if (openFo) {
                const locGid = openFo.assignedLocation?.location?.id || '';
                effectiveLocId = locGid.split('/').pop() || '';
            }
        }
        const perOrderSession = {
            ...baseSession,
            shopify_location_id: effectiveLocId,
            location_name:       (locationMap.get(String(effectiveLocId))?.location_name) || baseSession.location_name,
            store_key:           selectedOrderContext.orderStoreKey || baseSession.store_key,
        };
        // Wrap fetchFn: inherit per-order store_key AND inject X-Emulate-Location header.
        const perOrderFetchFn = (url, opts, storeKeyOverride = null) => {
            const mergedOpts = effectiveLocId ? {
                ...opts,
                headers: { ...(opts?.headers || {}), 'X-Emulate-Location': String(effectiveLocId) },
            } : opts;
            return fetchFn(url, mergedOpts, storeKeyOverride || selectedOrderContext.orderStoreKey);
        };
        const workingBanner = orderLocationName ? (
            <div className="working-at-banner">
                <span className="working-at-banner__icon">📍</span>
                {t('Working at:')} {orderLocationName}
            </div>
        ) : null;
        // (board variable removed — inline below)
        const participantsFooter = (
            <PortalParticipantsFooter
                orderId={selectedOrder.order_id}
                storeKey={selectedOrderContext.orderStoreKey}
                fetchFn={fetchFn}
                roles={['pickers', 'pressers']}
            />
        );
        const notifModal = (
            <NotificationBroadcastModal
                notifications={pendingNotifications}
                onAcknowledge={() => {
                    // Remember what we're clearing so a re-fetch of the same
                    // order (e.g. after label print) doesn't re-prompt.
                    _rememberAcked(
                        pendingNotifications,
                        selectedOrderContext?.orderStoreKey,
                        selectedOrder?.order_id,
                    );
                    setPendingNotifications([]);
                }}
            />
        );
        if (!fullscreen) {
            return (
                <div className="pp-page">
                    {notifModal}
                    <div className="pp-page-header">
                        <div>
                            <h1 className="pp-page-title">{t('Packing Portal')}</h1>
                            {headerLocationName && <div className="pp-page-loc">{headerLocationName}</div>}
                        </div>
                    </div>
                    {workingBanner}
                    {participantsFooter}
                    <QCBoard orderResult={orderDetail} session={perOrderSession} onExit={exitSession} notifications={orderNotifications} onReviewNotifications={() => setPendingNotifications(orderNotifications)} fetchFn={perOrderFetchFn} />
                </div>
            );
        }
        return <>{notifModal}{workingBanner}{participantsFooter}<QCBoard orderResult={orderDetail} session={perOrderSession} onExit={exitSession} notifications={orderNotifications} onReviewNotifications={() => setPendingNotifications(orderNotifications)} fetchFn={perOrderFetchFn} /></>;
    }

    // First-frame gate: when the URL specifies an order (label-close return-
    // nav, deep link), the initial render happens BEFORE the bootstrap
    // useEffect fires. Without this gate, that render shows the search list
    // for one frame, then flips to Loading once selectOrder → loadingOrder=true
    // lands. Ref-driven so it only ever intercepts the pre-effect render —
    // once the effect fires (ref=true), fall through to normal state flow
    // regardless of what selectOrder ends up doing (picker, error, etc).
    if (urlOrderId && !bootstrapFiredRef.current) {
        return <>{locPickerModal}<div className="qcp-loading">{t('Loading order…')}</div></>;
    }

    if (!fullscreen) {
        return (
            <div className="pp-page">
                <div className="pp-page-header">
                    <div>
                        <h1 className="pp-page-title">{t('Packing Portal')}</h1>
                        {headerLocationName && <div className="pp-page-loc">{headerLocationName}</div>}
                    </div>
                </div>
                {locPickerModal}
                {bootstrapError && <p className="pp-no-loc">{bootstrapError}</p>}
                <OrderSearch
                    session={baseSession}
                    presserSession={presserSession}
                    assignments={assignments}
                    locationMap={locationMap}
                    onSelectOrder={selectOrder}
                    sidebarMode
                    fetchFn={fetchFn}
                />
            </div>
        );
    }

    return (
        <>
            <NotificationBroadcastModal
                notifications={pendingNotifications}
                onAcknowledge={() => {
                    // Remember what we're clearing so a re-fetch of the same
                    // order (e.g. after label print) doesn't re-prompt.
                    _rememberAcked(
                        pendingNotifications,
                        selectedOrderContext?.orderStoreKey,
                        selectedOrder?.order_id,
                    );
                    setPendingNotifications([]);
                }}
            />
            {locPickerModal}
            {bootstrapError && <p className="pp-no-loc">{bootstrapError}</p>}
            <OrderSearch
                session={baseSession}
                presserSession={presserSession}
                assignments={assignments}
                locationMap={locationMap}
                onSelectOrder={selectOrder}
                fetchFn={fetchFn}
                belowInput={(
                    <PortalDailySummary
                        portal="packing"
                        presserSession={presserSession}
                        fetchFn={fetchFn}
                    />
                )}
            />
        </>
    );
}
